import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box, Paper, Typography } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { ReordenItem } from "./reorden.types";

interface Props {
  item: ReordenItem;
  posicion: number; // 1..N mostrado al usuario
}

/**
 * Tarjeta individual arrastrable (Zona A). Usa useSortable de @dnd-kit/sortable.
 * Muestra un drag handle (icono de seis puntos), la miniatura del banner, el
 * titulo y la posicion actual. Accesible por teclado via los listeners de dnd-kit.
 */
const SortableBannerCard: React.FC<Props> = ({ item, posicion }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.idDistribucion });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1, // vista previa semi-transparente al arrastrar
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={isDragging ? 6 : 1}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2.5,
        p: 2,
        mb: 1,
        borderRadius: 2,
        userSelect: "none",
      }}
      data-testid={`reorden-card-${item.idDistribucion}`}
    >
      <Box
        {...attributes}
        {...listeners}
        aria-label={`Arrastrar ${item.titulo}`}
        role="button"
        tabIndex={0}
        sx={{
          cursor: "grab",
          display: "flex",
          alignItems: "center",
          color: "text.secondary",
          "&:active": { cursor: "grabbing" },
        }}
      >
        <DragIndicatorIcon />
      </Box>

      <Box
        sx={{
          width: 96,
          height: 64,
          flexShrink: 0,
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: "grey.100",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {item.urlBanner ? (
          <img
            src={item.urlBanner}
            alt={item.titulo}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            sin img
          </Typography>
        )}
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body1" noWrap title={item.titulo}>
          {item.titulo || "(sin título)"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          posición {posicion}
        </Typography>
      </Box>
    </Paper>
  );
};

export default SortableBannerCard;
